<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { postMagicLinkLogin } from 'src/api/endpoints/loginWeb';
import { useActivationStore, useLocalizationStore } from 'src/store';
import { useSessionStore } from '@/store/sessionStore';
import { handleApiRequest } from 'src/api/handleApiRequest';
import Box from 'src/components/BoxContainer.vue';
import { useRouter } from 'vue-router';
import {useI18n} from "vue-i18n";
import { useQuasar, QForm } from 'quasar';
import Svg from '@/components/Svg.vue';
import { mymobilityWordmark } from '@/assets';
import { registerSyntheticMomentCultures } from '@/plugins/moment-cultured';
import { userLogout } from '@/services/userService';
import { custom_moment } from '@/plugins/moment-cultured';


const activationStore = useActivationStore(),
  router = useRouter(),
  {t} = useI18n(),
  sessionStore = useSessionStore(),
  $q = useQuasar(),

  props = defineProps({
    magicLinkToken: String,
  }),

// Reactive data
  isLoading = ref(true),
  loginModel = reactive({
    lastName: '',
    dateOfBirth: '',
    magicLinkToken: props.magicLinkToken || '',
    countryCode: '',
    utcOffset: sessionStore.utcOffset,
  }),
  showloginInfoModal = ref(false),
  validationError = ref(false);

// Computed properties
const countryList = computed(() => activationStore.supportedCountries),
  utcOffset = computed(() => sessionStore.utcOffset);

// Template Refs
const loginForm = ref<QForm | null>(null);

// Watchers
watch(() => countryList.value.selectedCountryCode, (newVal) => {
  loginModel.countryCode = newVal;
},
{ immediate: true }
);

watch(loginModel, () => {
  if (validationError.value) {
    loginForm.value?.resetValidation();
    validationError.value = false;
  }
});

const onSubmitSave = async () => {
  $q.loading.show();

  try {
    const dob = custom_moment(loginModel.dateOfBirth);
    const formattedDob = dob.isValid()
    ? dob.startOf('day').format('YYYY-MM-DD')
    : '';

    const postModel = {
      lastName: loginModel.lastName.trim(),
      dateOfBirth: formattedDob,
      magicLinkToken: loginModel.magicLinkToken,
      countryCode: loginModel.countryCode,
      utcOffset: loginModel.utcOffset
    };
    const response = await handleApiRequest(() => postMagicLinkLogin(postModel), null, $q);
    if (response && response.result) {
      sessionStore.setCompletedLogin(true);

      // reload local strings
      const localizationStore = useLocalizationStore();
      await localizationStore.loadLocalizedStrings();
      registerSyntheticMomentCultures(localizationStore.locale);

      activationStore.setRegistrationLastName(postModel.lastName);
      activationStore.setRegistrationDateOfBirth(loginModel.dateOfBirth);
      activationStore.setRegistrationMagicLink(loginModel.magicLinkToken);
      activationStore.supportedCountries.selectedCountryCode = loginModel.countryCode;
      
      var magicLinkEocId = response.result;
      if (magicLinkEocId > 0) {
        // Set the selected procedure in the session store
        sessionStore.setMagicLinkEocId(magicLinkEocId);
      }
        
      await router.push({ name: 'Authenticate' });
    }  
  } finally {
    $q.loading.hide();
  }

};

// Lifecycle hooks
onMounted(async () => {

  document.querySelectorAll('html')[0].scrollTop = 0;
  (document.querySelectorAll('input:first-child')[0] as HTMLElement).focus();

  sessionStore.setIsHeaderVisible(false);

  if (sessionStore.user) {
    await userLogout($q);
  }

  isLoading.value = false;
});

// Expose properties for template usage
defineExpose({
  loginModel,
  showloginInfoModal,
  countryList,
  utcOffset,
  onSubmitSave,
});
</script>

<template>
  <q-page class="no-header login-form" padding>
    <div class="row bs-gutter justify-center">

      <!-- Box -->
      <Box :box-bkgd-color="$zb.enums.BoxBackgroundColorEnum.linearGradient"
        :box-brdr-radius="$zb.enums.BoxBorderRadiusEnum.lg" :box-drop-shadow="$zb.enums.BoxDropShadowEnum.xl"
        :box-padding="$zb.enums.BoxPaddingEnum.p505065" :box-width="$zb.enums.BoxWidthEnum.md" class="col-auto">

        <!-- Heading -->
        <div class="row no-wrap bs-gutter items-center login-form__heading">
          <div class="col" />

          <!-- mymobility Wordmark -->
          <div class="col-auto mymo-wordmark">
            <Svg :vector-paths="mymobilityWordmark.vectorPaths" :view-box="mymobilityWordmark.viewBox" />
          </div>

          <!-- Question Circle Button -->
          <div class="col question">
            <zbm-skeleton-loader dark :loading="isLoading" size="30px">
              <zbm-btn-icon @click="showloginInfoModal = true" icon="fa-light fa-question-circle" size="30px"
                text-color="content-grd-bg-content" data-testid="questionButton" />
            </zbm-skeleton-loader>

            <!-- Question Circle Dialog -->
            <zbm-dialog v-model="showloginInfoModal" data-testid="loginHelpModal">

              <!-- Header -->
              <div class="row q-dialog__header">
                <div class="col-auto">

                  <!-- Question Circle Icon -->
                  <q-icon color="content-assets-hero-def" name="fa-solid fa-circle-question" size="42px" />
                </div>
              </div>

              <!-- Body -->
              <div class="row q-dialog__body">
                <div class="col">
                  <h3 class="text-weight-semibold q-mb-lg">{{ t('log_in_help') }}</h3>
                  <p>
                    {{ t('login_auth_help_ios') }}
                    <a href="https://support.zbmymobilitysolutions.com/start/" target="_blank" data-testid="supportSiteLink">
                      {{ t('login_login_help_visit_support_website_ios') }}
                    </a>
                  </p>
                </div>
              </div>

              <!-- Footer (CTA button(s)) -->
              <div class="row q-dialog__footer">

                <!-- Okay Button -->
                <div class="col-auto">
                  <zbm-btn v-close-popup :color="$zb.enums.ButtonStdColorEnum.accentText"
                    :height="$zb.enums.ButtonHeightEnum.lg" :label="t('gen_okay')" data-testid="loginHelpOkayButton"/>
                </div>
              </div>
            </zbm-dialog>
          </div>
        </div>

        <!-- Log In Form -->
        <q-form @submit.prevent="onSubmitSave" @validation-error="validationError = true" ref="loginForm">

            <!-- Last Name -->
            <div class="row justify-center mb-40">
                <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                    <zbm-input-with-validation v-model="loginModel.lastName" dark :placeholder="t('LastName')"
                                               :rules="[$zb.validators.required()]" lazyRules="ondemand" />
                </zbm-skeleton-loader>
            </div>

            <!-- Date Of Birth -->
            <div class="row justify-center mb-40">
              <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                <zbm-date-picker v-model="loginModel.dateOfBirth" :placeholder="t('activate_date_of_birth')"
                  :rules="[$zb.validators.required()]" dark :maxDate="custom_moment().toDate()"/>
              </zbm-skeleton-loader>
            </div>

            <!-- Country Selector -->
            <div class="row flex-center country">

                <!-- Label -->
                <div class="col-auto font-size-20 text-content-grd-bg-content text-no-wrap">
                    <zbm-skeleton-loader dark height="30px" :loading="isLoading" width="107px">
                        {{ t('provider_select_country_button') }}
                    </zbm-skeleton-loader>
                </div>
                <div class="flex-br" />

                <!-- Select -->
                <zbm-skeleton-loader :class="$zb.enums.FormElWidthEnum.qFormEl300" dark height="40px" :loading="isLoading">
                    <zbm-select-with-validation v-model="loginModel.countryCode" dark
                                                :name="t('provider_select_country_button')" option-label="text" :options="countryList.countryOptions"
                                                option-value="value" :rules="[$zb.validators.required()]" />
                </zbm-skeleton-loader>
            </div>

            <!-- Log In Button -->
            <div class="row justify-center">
                <zbm-skeleton-loader :class="$zb.enums.ButtonWidthEnum.w300" dark height="44px" :loading="isLoading">
                    <zbm-btn class="glow--white" :color="$zb.enums.ButtonStdColorEnum.darkTeal"
                             :height="$zb.enums.ButtonHeightEnum.lg" :label="t('log_in')" :type="$zb.enums.ButtonTypeEnum.submit"
                             :width="$zb.enums.ButtonWidthEnum.w300" data-testid="loginButton" />
                </zbm-skeleton-loader>
            </div>
        </q-form>
      </Box>
    </div>
  </q-page>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use "sass:math";

.login-form { // .q-page

  // Heading
  &__heading { // .row
    margin-block-end: 118px;

    // mymobility Wordmark
    .mymo-wordmark { // [class*="col"]
      line-height: 0;
      color: map.get($co-brand-clrs, content-grd-bg-content);

      svg {
        width: 100%;
        max-width: 240px;
        height: auto;
      }
    }

    // Question Circle Button
    .question { // [class*="col"]
      text-align: end;
    }
  }

  // Form
  .q-form { // <form>
    margin-block-end: 26px;

    // Country Selector
    .country { // .row
      margin-block-end: 54px;
    }
  }

  @media (max-width: 479px) {

    // Heading
    &__heading { // .row

      > [class*="col"]:first-child {
        display: none;
      }

      // mymobility Wordmark
      .mymo-wordmark { // [class*="col"]
        flex: 10000 1 0;
      }

      // Question Circle Button
      .question { // [class*="col"]
        flex: 0 0 auto
      }
    }
  }

  @media (min-width: 600px) {

    // Form
    .q-form {

      // Country Selector
      .country {

        .flex-br {
          display: none;
        }

        [class*=col]:first-child {
          padding-inline-end: 10px;
        }
      }
    }
  }
}
</style>
